import { v, type Infer } from "convex/values";

export const exportArtifactOwnerKindValidator = v.union(
  v.literal("takeoffPilotJob"),
  v.literal("fieldPilotJob"),
  v.literal("revPilotComparison"),
  v.literal("specPilotSubmittal")
);

/** Exports built in the browser. Their artifacts hold the name and options only, no file. */
export const browserExportKindValidator = v.union(
  v.literal("spec_pilot_submittal_csv"),
  v.literal("spec_pilot_tracker_csv"),
  v.literal("spec_pilot_tracker_workbook"),
  v.literal("takeoff_csv"),
  v.literal("revpilot_annotated_pdf")
);

export const exportArtifactKindValidator = v.union(
  v.literal("takeoff_pdf"),
  v.literal("takeoff_workbook"),
  v.literal("field_pilot_pdf"),
  v.literal("revpilot_combined_pdf"),
  v.literal("spec_pilot_submittal_pdf"),
  v.literal("spec_pilot_tracker_pdf"),
  browserExportKindValidator
);

export type ExportArtifactKind = Infer<typeof exportArtifactKindValidator>;
export type BrowserExportKind = Infer<typeof browserExportKindValidator>;

/** Typed owner reference so each kind is validated against its own table. */
export const exportArtifactOwnerValidator = v.union(
  v.object({ kind: v.literal("specPilotSubmittal"), id: v.id("specPilotSubmittals") }),
  v.object({ kind: v.literal("takeoffPilotJob"), id: v.id("takeoffPilotJobs") }),
  v.object({ kind: v.literal("fieldPilotJob"), id: v.id("fieldPilotJobs") }),
  v.object({ kind: v.literal("revPilotComparison"), id: v.id("revPilotScheduleComparisons") })
);

export type ExportArtifactOwner = Infer<typeof exportArtifactOwnerValidator>;

export const EXPORT_FILENAME_MAX_LENGTH = 120;

// Characters Windows and macOS reject in filenames, plus control characters.
// eslint-disable-next-line no-control-regex
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|\u0000-\u001f\u007f]/g;

/** Normalizes a user-entered export file name (no extension); "" when nothing usable remains. */
export function normalizeExportFilename(value: string): string {
  return value
    .replace(INVALID_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+|\.+$/g, "")
    .trim()
    .slice(0, EXPORT_FILENAME_MAX_LENGTH)
    .trim();
}

type TrackerScopeOptions = {
  scope?: "single" | "all";
  trackerType?: string;
  customTrackerName?: string;
};

function trackerTargetKey(options: TrackerScopeOptions) {
  if (options.scope === "all" || !options.trackerType) return "trackers";
  if (options.trackerType === "custom") return `tracker:custom:${options.customTrackerName ?? ""}`;
  return `tracker:${options.trackerType}`;
}

/**
 * The deliverable an export produces. Formats of one deliverable (a tracker's PDF and CSV,
 * a takeoff's CSV, Excel and PDF) share a key, and so share a saved file name.
 */
export function exportNameTargetKey(exportKind: ExportArtifactKind, options: object): string {
  switch (exportKind) {
    case "spec_pilot_submittal_pdf":
    case "spec_pilot_submittal_csv":
      return "submittal_log";
    case "spec_pilot_tracker_pdf":
    case "spec_pilot_tracker_csv":
      return trackerTargetKey(options as TrackerScopeOptions);
    case "spec_pilot_tracker_workbook":
      return "trackers";
    case "takeoff_pdf":
    case "takeoff_workbook":
    case "takeoff_csv":
      return "takeoff";
    case "field_pilot_pdf":
      return "field_report";
    case "revpilot_combined_pdf":
      return "revision_report";
    case "revpilot_annotated_pdf":
      return "annotated_plans";
  }
}
